import { useEffect, useState } from 'react'
import axios from 'axios'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import cn from 'utils/cn'
import Icon from 'core/components/icons/Icon'
import { BinaryData } from 'types/utility'

interface PdfViewerProps {
  src?: string | BinaryData | File
  className?: string
  fileName?: string
}

const PdfViewer = ({
  src,
  className,
  fileName = 'pdf-attachment-'
}: PdfViewerProps) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const { data, status, fetchStatus } = useQueryCallbacks<BinaryData>({
    queryKey: ['binary', src],
    queryFn: () => {
      return axios
        .get(src as string, { responseType: 'blob' })
        .then((response) => response.data)
    },
    enabled: typeof src === 'string',
    // Note: cannot set this to Infinity, will cause bug
    staleTime: 0,
    onSuccess: (data) => {
      const blob = new Blob([data as BlobPart], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      setDataUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return url
      })
    }
  })

  useEffect(() => {
    if (typeof src === 'string' || !src) return
    const blob: Blob =
      src instanceof Blob
        ? src
        : new Blob([src as BlobPart], { type: 'application/pdf' })

    const url = URL.createObjectURL(blob)
    setDataUrl(url)

    // Fixme: memory leak when binary changes and new url are generated
  }, [data, fileName, src])

  useEffect(() => {
    return () => {
      if (dataUrl) URL.revokeObjectURL(dataUrl)
    }
  })

  // v5: status === 'pending' is true for disabled queries too. Gate on
  // fetchStatus to ensure we only show the spinner during an actual fetch.
  if (!dataUrl)
    return status === 'pending' && fetchStatus === 'fetching' ? (
      <div className="flex justify-center items-center h-48">
        <Icon name="loading" size={24} className="text-primary" />
      </div>
    ) : (
      <div className={className + ' bg-white flex justify-center items-center'}>
        <div className="w-[80%] flex flex-col items-center">
          <p className="text-xl font-semibold mb-4 text-danger">Ooops...</p>
          <p>
            Unfortunately, we cannot provide you with a review of the file{' '}
            {fileName}.pdf
          </p>
        </div>
      </div>
    )

  return (
    <div className={cn('relative', className)}>
      <iframe
        src={dataUrl + '#toolbar=0'}
        frameBorder="0"
        title={`${fileName} preview`}
        className="w-full h-full"
      >
        <object data={dataUrl}>
          <div>Your browser does not include online PDF viewer</div>
          <div>
            Please download the file and open it with a pdf viewer, such as
            Adobe Acrobatics.
          </div>
        </object>
      </iframe>
      <div className="absolute p-4 bottom-0 left w-full">
        <a
          href={dataUrl}
          download={fileName + '.pdf'}
          className="shadow bg-primary rounded-md p-2 hover:bg-primary! text-white!"
        >
          Download
        </a>
      </div>
    </div>
  )
}

export default PdfViewer
